import { useCallback } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { CLOSE_DIALOG_EVENT } from '@rio-cloud/rio-uikit/FrameDialog';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

import { getDaysInYearsAndMonths } from '@/features/battery/utils/DateUtils';
import { useBatteryOverviewTooltips } from '@/features/battery/workshop/useBatteryOverviewTooltips';
import { getSoHStatusClass, NoData } from '@/features/batteryOverview/utils/BatteryDataUtils';
import { BatteryPack } from '@/generated/GeneratedApiTypes';
import SoHValue from './SoHValue';

const BATTERY_SERVICE_DIALOGS = 'BATTERY-SERVICE-DIALOGS';
const KWH = ' kWh';
const KM = ' km';

interface MetricPanelProps {
    titleId: string;
    tooltip?: React.ReactElement;
    children: React.ReactNode;
}

const MetricPanel = ({ titleId, tooltip, children }: MetricPanelProps) => (
    <div className="panel panel-default margin-bottom-0 width-100pct">
        <div className="panel-body">
            <div className="display-flex align-items-center">
                <FormattedMessage id={titleId} />
                {tooltip && (
                    <OverlayTrigger placement="right" overlay={tooltip}>
                        <span className="rioglyph rioglyph-exclamation-sign text-color-dark margin-left-10" />
                    </OverlayTrigger>
                )}
            </div>
            <div className="margin-top-20">{children}</div>
        </div>
    </div>
);

const NumericValue = ({ value, unit }: { value: number | undefined; unit: string }) =>
    value !== undefined ? (
        <span className="text-size-18">
            <FormattedNumber value={value} />
            {unit}
        </span>
    ) : (
        <NoData />
    );

interface PackDetailViewProps {
    pack: BatteryPack;
    assetId: string;
}

const PackLevelDetailView = ({ pack, assetId }: PackDetailViewProps) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { sendMessageToParent } = usePostMessageSender();

    const {
        id,
        state_of_health_percentage,
        state_of_health_status,
        full_charge_energy,
        discharging_energy_throughput,
        mileage,
        usage_days,
    } = pack;

    const systemStateOfHealthStatus = state_of_health_status?.toUpperCase() ?? 'NO_DATA';
    const { fullChargeEnergyTooltip, energyThroughputTooltip } =
        useBatteryOverviewTooltips(false, false, systemStateOfHealthStatus);

    const handleCloseDialog = useCallback(() => {
        sendMessageToParent({
            type: `${BATTERY_SERVICE_DIALOGS}${CLOSE_DIALOG_EVENT}`,
            payload: 'Closed Pack Detail dialog',
        });
    }, [sendMessageToParent]);

    return (
        <>
            <div
                className="display-flex justify-content-between align-items-center padding-10 border-style-solid
                            border-bottom-only border-width-1 border-color-light"
            >
                <div className="display-flex align-items-center">
                    <span className="text-size-22">
                        <FormattedMessage id="intl-msg:batteryService.batteryPackLevel.details" />
                    </span>
                </div>
                <button
                    type="button"
                    className="btn btn-icon-only btn-muted close"
                    onClick={handleCloseDialog}
                    aria-label="dialog close button"
                >
                    <span className="rioglyph rioglyph-remove" aria-hidden="true" />
                </button>
            </div>
            <button
                type="button"
                className="btn btn-link text-color-darkest text-decoration-none margin-top-15"
                onClick={() => navigate(`/dialog/pack-levels/${assetId}`)}
            >
                <span
                    className="rioglyph rioglyph-arrow-left text-color-primary margin-right-5 text-size-12"
                    aria-hidden="true"
                />
                <FormattedMessage id="intl-msg:common.back" />
            </button>
            <div className="padding-left-25 padding-top-15">
                <span>
                    <FormattedMessage id="intl-msg:batteryService.batteryPackLevel.batteryPack" /> {id}
                </span>
            </div>
            <Dialog.Body className="padding-15">
                <div className="display-grid gap-10" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className={'list-group border'}>
                        <div
                            className={`panel panel-default margin-bottom-0 width-100pct height-100pct border-top 
                                list-group-item list-group-item-${getSoHStatusClass(state_of_health_status)}`}
                        >
                            <div className="margin-bottom-25 margin-top-5 text-color-darkest">
                                <FormattedMessage id="intl-msg:batteryService.batteryStateOfHealth.title" />
                            </div>
                            <SoHValue
                                status={systemStateOfHealthStatus}
                                percentage={state_of_health_percentage}
                                noDataClassName="margin-top-20"
                                valueClassName="text-color-darkest"
                            />
                        </div>
                    </div>
                    <MetricPanel titleId="intl-msg:batteryService.batteryPackLevel.packAge">
                        <span className="text-size-18">
                            {usage_days !== undefined ? getDaysInYearsAndMonths(usage_days, intl) : <NoData />}
                        </span>
                    </MetricPanel>
                    <MetricPanel titleId="intl-msg:batteryService.batteryPackLevel.packMileage">
                        <NumericValue value={mileage} unit={KM} />
                    </MetricPanel>
                    <MetricPanel titleId="intl-msg:batteryService.fullChargeEnergy" tooltip={fullChargeEnergyTooltip}>
                        <NumericValue value={full_charge_energy} unit={KWH} />
                    </MetricPanel>
                    <MetricPanel
                        titleId="intl-msg:batteryService.dischargedEnergyThroughput"
                        tooltip={energyThroughputTooltip}
                    >
                        <NumericValue value={discharging_energy_throughput} unit={KWH} />
                    </MetricPanel>

                </div>
            </Dialog.Body>
        </>
    );
};

export default PackLevelDetailView;
